import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Phone, X } from 'lucide-react';

/**
 * Sticky Mobile "Call Now" CTA - Fixed to bottom of screen on mobile devices.
 * High-impact for local SEO conversions.
 * Hidden on desktop (md+) and when user scrolls to footer.
 */
const MobileCallCTA = () => {
  const { pathname } = useLocation();
  const [isVisible, setIsVisible] = useState(true);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Check if user previously dismissed (session only)
    const dismissed = sessionStorage.getItem('mobileCtaDismissed');
    if (dismissed) {
      setIsDismissed(true);
    }

    // Hide when near footer to avoid overlap
    const handleScroll = () => {
      const footer = document.querySelector('footer');
      if (footer) {
        const footerRect = footer.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        // Hide CTA when footer is visible
        setIsVisible(footerRect.top > windowHeight - 100);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDismissed(true);
    sessionStorage.setItem('mobileCtaDismissed', 'true');
  };

  // Don't render on dismissed or hidden, or inside admin tools
  if (isDismissed || !isVisible || pathname.startsWith('/admin') || pathname.startsWith('/support/admin') || pathname.startsWith('/savings-portal') || pathname.startsWith('/upload/')) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 md:hidden safe-area-pb"
      data-testid="mobile-call-cta"
    >
      {/* Gradient shadow above */}
      <div className="h-4 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
      
      {/* Main CTA Bar */}
      <div className="bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 px-4 py-3 shadow-lg">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          {/* Call Button */}
          <a
            href="tel:6479513653"
            className="flex-1 flex items-center justify-center gap-3 bg-black text-white px-6 py-3 rounded-full font-bold text-base hover:bg-gray-900 transition-all active:scale-95"
            data-testid="mobile-call-button"
          >
            <Phone className="w-5 h-5 animate-pulse" />
            <span>Call Now: 647-951-3653</span>
          </a>
          
          {/* Dismiss Button */}
          <button
            onClick={handleDismiss}
            className="ml-3 p-2 text-black/70 hover:text-black transition-colors"
            aria-label="Dismiss call button"
            data-testid="mobile-call-dismiss"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Subtle text */}
        <p className="text-center text-black/70 text-xs mt-1 font-medium">
          Free Consultation • Toronto IT Support
        </p>
      </div>
    </div>
  );
};

export default MobileCallCTA;
